import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { AdaButton } from '@betrybe/ada-react';
import * as Sentry from '@sentry/browser';

import { styled } from '@/configs/stitches.config';
import { StudentContext } from '@/contexts';
import { DefaultLayout } from '@/layouts';
import { useClient } from '@/hooks';
import { StatesEnum } from '@/utils';
import { useMscPipeline } from '@/services/pre-enrollment';
import PaymentMethodCard from './PaymentMethodCard';

const Root = styled('div', {
  display: 'flex',
  gap: '$spacingXl',
  flexDirection: 'column',

  '@lg': {
    flexDirection: 'row',
  },
});

const Content = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$spacingMd',
});

const Title = styled('h1', {
  textWrap: 'balance',
});

const ActionsContainer = styled('div', {
  display: 'flex',
  marginTop: '$spacingMd',
  gap: '$spacingMd',
  flexWrap: 'wrap',
  justifyContent: 'center',

  '@lg': {
    justifyContent: 'flex-start',
  },
});

const Illustration = styled('img', {
  display: 'none',

  '@lg': {
    alignSelf: 'center',
    display: 'block',
    maxWidth: '20rem',
  },
});

const MscDisapproval: React.FC = () => {
  const { state } = useContext(StudentContext);
  const { clientId, mscPipelineId, selectionProcessId } = useClient();
  const { changeMscPipeline } = useMscPipeline();
  const history = useHistory();

  const handleChangePaymentMethod = async (
    event: StatesEnum,
    /* eslint-disable @typescript-eslint/no-explicit-any */
    otherEventFields?: any,
  ) => {
    try {
      await changeMscPipeline({
        mscPipelineId,
        selectionProcessId,
        event,
        otherEventFields,
      });

      if (event === StatesEnum.CHANGE_FINANCING_TYPE) {
        history.push('/register/approval');
        return;
      }

      window.location.href = process.env.REACT_APP_EDUCATION_URL;
    } catch (error) {
      Sentry.setUser({ id: clientId });
      Sentry.captureException(error);
      history.push('/error');
    }
  };

  return (
    <DefaultLayout cardSize="big-card">
      <Root>
        <Content>
          <Title>Sua análise de crédito não foi aprovada para o MSC</Title>
          <p>Ei, {state.clientData.name}!</p>

          <p>
            Agradecemos seu interesse em utilizar o método de pagamento do
            Modelo de Sucesso Compartilhado para estudar na Trybe!
          </p>
          <p>
            Neste momento, sua análise de crédito <b>não foi aprovada.</b>
          </p>
          {state.hasFifteenDelayApproval && (
            <>
              <p>
                Mas não se preocupe! Temos outra opção para você financiar seus
                estudos.{' '}
                <b>
                  Sua análise pré-aprovou o Pagamento Facilitado com Carência.
                </b>{' '}
                Saiba mais ao lado.
              </p>
              <p>
                Além disso, você ainda tem a possibilidade de escolher os
                métodos de pagamento tradicionais - à vista ou parcelado, para
                continuar sua jornada conosco.
              </p>
            </>
          )}
          {!state.hasFifteenDelayApproval && (
            <p>
              Mas não se preocupe! Você ainda tem a possibilidade de escolher
              outros métodos de pagamento para continuar sua jornada conosco.
            </p>
          )}
        </Content>
        {state.hasFifteenDelayApproval && <PaymentMethodCard />}
        {!state.hasFifteenDelayApproval && (
          <Illustration src="/assets/images/disapproval.svg" />
        )}
      </Root>

      <ActionsContainer>
        {state.hasFifteenDelayApproval && (
          <AdaButton
            onClick={() =>
              handleChangePaymentMethod(StatesEnum.CHANGE_FINANCING_TYPE, {
                new_payment_method: 'fifteen_delay',
              })
            }
          >
            Seguir com Financiamento com Carência
          </AdaButton>
        )}
        <AdaButton
          onClick={() =>
            handleChangePaymentMethod(StatesEnum.CHANGE_PAYMENT_METHOD)
          }
          kind={state.hasFifteenDelayApproval ? 'secondary' : 'primary'}
        >
          Escolher outro método de pagamento
        </AdaButton>
      </ActionsContainer>
    </DefaultLayout>
  );
};

export default MscDisapproval;
